import { useState, useEffect, useRef, use } from "react";
import OvenPlayer from 'ovenplayer';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';

export default function App() {
  const [log, setLog] = useState("Console output");
  const [playerReady, setPlayerReady] = useState(false);
  const playerContainerRef = useRef(null);
  const [dronePosition, setDronePosition] = useState({ lat: 0, lon: 0 });
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const marker = new mapboxgl.Marker({ color: '#F84C4C' });
  const [detectedObjects , setDetectedObjects] = useState([]);

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESSTOKEN;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
    });

    return () => {
      mapRef.current.remove();
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && (dronePosition.lat !== 0 || dronePosition.lon !== 0)) {
      console.log('updating map with drone position:', dronePosition);
      mapRef.current.setCenter([dronePosition.lon, dronePosition.lat]);
      mapRef.current.setZoom(15);
    
      marker.setLngLat([dronePosition.lon, dronePosition.lat])
        .addTo(mapRef.current);
    }
  }, [dronePosition]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!playerReady) return;

      try {
        const response = await fetch(process.env.REACT_APP_EXPRESS_BACKEND_URL + 'detectedobjects');
        const body = await response.json();
        setLog(`${body.objects.length} objecten gedetecteerd`);
        setDetectedObjects(body.objects);
      } catch (error) {
        setLog("Error fetching detected objects");
        console.error("Error fetching detected objects:", error);
      }

      try {
        const response = await fetch(process.env.REACT_APP_EXPRESS_BACKEND_URL + 'droneposition');
        const body = await response.json();

        if (
          Math.abs(body.position.lat - dronePosition.lat) > 0.000001 ||
          Math.abs(body.position.lon - dronePosition.lon) > 0.000001
        ) {
          setDronePosition({
            lat: body.position.lat,
            lon: body.position.lon
          });
        }
      } catch (error) {
        setLog("Error fetching drone position");
        console.error("Error fetching drone position:", error);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [playerReady, dronePosition]);


  useEffect(() => {
    fetch(process.env.REACT_APP_EXPRESS_BACKEND_URL)
      .then((res) => res.json())
      .then((data) => setLog(data.message))
      .catch(() => setLog("Error fetching data"));
  }, []);

  const handlePlayStream = () => {
    if (!playerContainerRef.current) return;

    OvenPlayer.create(playerContainerRef.current, {
      autoStart: true,
      mute: false,
      controls: true,
      sources: [{
        type: 'webrtc',
        file: process.env.REACT_APP_STREAM_SRC_URL
      }]
    });

    setPlayerReady(true);
    setLog("WebRTC stream gestart.");
  };

  // Utility to assign a color to each class
  const classColors = useRef({});
  const colorPalette = [
    "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231",
    "#911eb4", "#46f0f0", "#f032e6", "#bcf60c", "#fabebe",
  ];
  const colorIndex = useRef(0);

  useEffect(() => {
    detectedObjects.forEach(obj => {
      const className = obj.class;
      if (!classColors.current[className]) {
        classColors.current[className] = colorPalette[colorIndex.current % colorPalette.length];
        colorIndex.current++;
      }
    });
  }, [detectedObjects]);

  // Group by class and get distinct objects per class
  const groupedObjects = {};
  detectedObjects.forEach(obj => {
    if (!groupedObjects[obj.class]) groupedObjects[obj.class] = [];
    // Only add if not already present (by id)
    if (!groupedObjects[obj.class].some(o => o.id === obj.id)) {
      groupedObjects[obj.class].push(obj);
    }
  });

  return (
    <div className="container">
      <div className="sub-container">
        <div className="image-container">
          <div className="image-box">
            <div className="image" ref={playerContainerRef} />
          </div>
        </div>

        <div className="object-selection-container">
          <h2 id="object-selection-title">Object selectie</h2>
          <div className="object-selection-box">
            {Object.keys(groupedObjects).length > 0 ? (
              Object.entries(groupedObjects).map(([className, objects]) => (
                <div key={className} className="object-class-group">
                  <div
                    className="object-class-label"
                    style={{ color: classColors.current[className] }}
                  >
                    {className}
                  </div>
                  {objects.map((object) => (
                    <div
                      key={object.id}
                      className="object-item"
                      style={{ borderLeft: `4px solid ${classColors.current[className]}` }}
                    >
                      <span className="object-id">{object.id}</span>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p>Geen objecten gedetecteerd.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bottom-section">
        <div className="box" ref={mapContainerRef} />

        <div className="box">
          <h2>Start tracking</h2>
          <button disabled={playerReady} onClick={handlePlayStream}>Start</button>
        </div>

        <div className="box">
          <h2>Console</h2>
          <div className="console">{log}</div>
        </div>
      </div>
    </div>
  );
}
